import {
    IonBadge,
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonModal,
    IonRadio,
    IonRadioGroup,
    IonRow,
    IonSearchbar,
    IonSpinner,
    IonTitle,
    IonToolbar,
    useIonAlert,
    useIonToast,
} from '@ionic/react';
import { closeOutline, createOutline, diceOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { apiBaseUrl } from '../../configs/app';
import loginService from '../../services/loginService';
import Pagination from '../../components/Pagination';
import './AdminPages.scss';

interface AdminUser {
    id: number;
    name: string;
    phone: string;
    created_at: string;
    roles: string[];
}

const authHeaders = () => ({
    Authorization: 'Bearer ' + loginService.currentUser.token,
    Accept: 'application/json',
    'Content-Type': 'application/json',
});

const AdminUsersPage: React.FC = () => {
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    // Edit modal
    const [editUser, setEditUser] = useState<AdminUser | null>(null);
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editRole, setEditRole] = useState<'admin' | 'user'>('user');
    const [saving, setSaving] = useState(false);

    const [presentToast] = useIonToast();
    const [presentAlert] = useIonAlert();
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        loadUsers(page, search);
    }, [page]);

    function loadUsers(p: number, s: string) {
        setLoading(true);
        const params = new URLSearchParams({ page: String(p) });
        if (s) params.set('search', s);
        fetch(apiBaseUrl + 'admin/users?' + params.toString(), { headers: authHeaders() })
            .then((r) => {
                if (!r.ok) throw new Error('HTTP ' + r.status);
                return r.json();
            })
            .then((data) => {
                setUsers(Array.isArray(data.data) ? data.data : []);
                setLastPage(data.last_page ?? 1);
            })
            .catch((err) => {
                console.error('Failed to load users', err);
                presentToast({ message: 'Failed to load users: ' + err.message, duration: 3000, color: 'danger' });
            })
            .finally(() => setLoading(false));
    }

    function handleSearch(val: string) {
        setSearch(val);
        setPage(1);
        loadUsers(1, val);
    }

    function openEdit(user: AdminUser) {
        setEditUser(user);
        setEditName(user.name);
        setEditPhone(user.phone);
        setEditPassword('');
        setEditRole(user.roles.includes('admin') ? 'admin' : 'user');
        modal.current?.present();
    }

    function generateRandomPassword() {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
        const length = 12;
        let generated = '';
        for (let i = 0; i < length; i += 1) {
            generated += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setEditPassword(generated);
        presentToast({ message: 'Random password generated', duration: 1500, color: 'success' });
    }

    function saveUser() {
        if (!editUser) return;
        setSaving(true);
        fetch(apiBaseUrl + 'admin/users/' + editUser.id + '/update', {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
                name: editName,
                phone: editPhone,
                password: editPassword || undefined,
                roles: [editRole],
            }),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.id) {
                    presentToast({ message: 'User updated successfully', duration: 2000, color: 'success' });
                    modal.current?.dismiss();
                    loadUsers(page, search);
                } else {
                    const msgs = Object.values(data.message ?? {}).flat().join(', ');
                    presentToast({ message: msgs || 'Failed to update user', duration: 3000, color: 'danger' });
                }
            })
            .finally(() => setSaving(false));
    }

    function confirmDelete(user: AdminUser) {
        presentAlert({
            header: 'Delete User',
            message: `Are you sure you want to delete "${user.name}"? This cannot be undone.`,
            buttons: [
                { text: 'Cancel', role: 'cancel' },
                {
                    text: 'Delete',
                    role: 'destructive',
                    handler: () => deleteUser(user.id),
                },
            ],
        });
    }

    function deleteUser(id: number) {
        fetch(apiBaseUrl + 'admin/users/' + id, {
            method: 'DELETE',
            headers: authHeaders(),
        })
            .then((r) => r.json())
            .then(() => {
                presentToast({ message: 'User deleted', duration: 2000, color: 'success' });
                loadUsers(page, search);
            })
            .catch(() => presentToast({ message: 'Failed to delete user', duration: 3000, color: 'danger' }));
    }

    return (
        <AdminLayout title="Users">
            <IonGrid>
                <IonRow>
                    <IonCol size="12">
                        <div className="admin-filter-bar">
                            <h3 style={{ margin: 0, color: '#1e293b' }}>All Users</h3>
                            <IonSearchbar
                                value={search}
                                onIonChange={(e) => handleSearch(e.detail.value ?? '')}
                                placeholder="Search by name or phone"
                                style={{ maxWidth: 320 }}
                            />
                        </div>

                        <div className="admin-table-container">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: 40 }}>
                                    <IonSpinner />
                                </div>
                            ) : (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Phone</th>
                                            <th>Roles</th>
                                            <th>Joined</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 && (
                                            <tr>
                                                <td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>
                                                    No users found
                                                </td>
                                            </tr>
                                        )}
                                        {users.map((user) => (
                                            <tr key={user.id}>
                                                <td className="text-center">#{user.id}</td>
                                                <td>{user.name}</td>
                                                <td>{user.phone}</td>
                                                <td>
                                                    {user.roles.map((r) => (
                                                        <IonBadge key={r} color={r === 'admin' ? 'danger' : 'medium'} style={{ marginRight: 4 }}>
                                                            {r}
                                                        </IonBadge>
                                                    ))}
                                                </td>
                                                <td className="text-center">{new Date(user.created_at).toDateString()}</td>
                                                <td className="text-center">
                                                    <IonButton fill="outline" size="small" onClick={() => openEdit(user)}>
                                                        <IonIcon icon={createOutline} slot="icon-only" />
                                                    </IonButton>
                                                    <IonButton fill="outline" size="small" color="danger" onClick={() => confirmDelete(user)} style={{ marginLeft: 4 }}>
                                                        <IonIcon icon={trashOutline} slot="icon-only" />
                                                    </IonButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {lastPage > 1 && (
                            <div style={{ marginTop: 20 }}>
                                <Pagination active={page} disabled={loading} onClick={setPage} buttonCount={lastPage} />
                            </div>
                        )}
                    </IonCol>
                </IonRow>
            </IonGrid>

            {/* Edit Modal */}
            <IonModal ref={modal} initialBreakpoint={1} breakpoints={[0, 1]}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Edit User #{editUser?.id}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={() => modal.current?.dismiss()}>
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <IonItem>
                        <IonLabel position="stacked">Name</IonLabel>
                        <IonInput value={editName} onIonChange={(e) => setEditName(e.detail.value ?? '')} />
                    </IonItem>
                    <IonItem>
                        <IonLabel position="stacked">Phone</IonLabel>
                        <IonInput value={editPhone} onIonChange={(e) => setEditPhone(e.detail.value ?? '')} />
                    </IonItem>
                    <IonItem>
                        <IonLabel position="stacked">New Password (leave blank to keep current)</IonLabel>
                        <IonInput type="password" value={editPassword} onIonChange={(e) => setEditPassword(e.detail.value ?? '')} />
                    </IonItem>
                    <div style={{ padding: '8px 16px 0', textAlign: 'right' }}>
                        <IonButton fill="outline" size="small" onClick={generateRandomPassword}>
                            <IonIcon icon={diceOutline} slot="start" />
                            Generate Random Password
                        </IonButton>
                    </div>

                    <div style={{ padding: '12px 16px' }}>
                        <IonLabel style={{ fontSize: 14, color: '#64748b', display: 'block', marginBottom: 8 }}>Role</IonLabel>
                        <IonRadioGroup value={editRole} onIonChange={(e) => setEditRole((e.detail.value as 'admin' | 'user') ?? 'user')}>
                            <IonItem lines="none">
                                <IonRadio slot="start" value="admin" />
                                <IonLabel>admin</IonLabel>
                            </IonItem>
                            <IonItem lines="none">
                                <IonRadio slot="start" value="user" />
                                <IonLabel>user</IonLabel>
                            </IonItem>
                        </IonRadioGroup>
                    </div>

                    <div style={{ padding: 16 }}>
                        <IonButton expand="block" onClick={saveUser} disabled={saving}>
                            {saving ? <IonSpinner name="lines-small" /> : 'Save Changes'}
                        </IonButton>
                    </div>
                </IonContent>
            </IonModal>
        </AdminLayout>
    );
};

export default AdminUsersPage;
